import { FormControl, FormLabel, FormGroup, Typography, Grid, Tooltip } from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { visuallyHidden } from '@mui/utils'

import TextInput from '../common/TextInput'
import CheckboxInput from '../common/CheckboxInput'
import PhoneNumberInput from '../common/PhoneNumberInput'
import IconButtonWithProgress from '../common/IconButtonWithProgress'

import intl from '../../helper/intl'
import validate from '../../helper/validate'

import useStyles from './PhoneFieldset.styles'

const PhoneFieldset = ({
  id,
  className,
  legend,
  autoFocus,
  disabled,
  phoneNumberConfirmed,
  onConfirmPhoneNumber,
  alias,
  countryCode,
  phoneNumber,
  phoneNumberSms,
  onChange,
  onDelete,
  showDeleteProgress
}) => {
  const classes = useStyles()

  const onPhoneFieldChange = (paramName, paramValue) => {
    if (typeof onChange === 'function') {
      const phoneObj = { id, alias, countryCode, phoneNumber, phoneNumberSms, phoneNumberConfirmed }
      phoneObj[paramName] = paramValue
      onChange(phoneObj)
    }
  }

  return (
    <FormControl
      className={classes.wrapper + (className ? ' ' + className : '')}
      id={'phone-fieldset-' + id}
      component="fieldset"
      role="group"
    >
      {/* Legend of the fieldset, must be the first child of the fieldset. For screen readers only. */}
      {legend && (
        <Typography style={visuallyHidden} component="legend">
          {legend}
        </Typography>
      )}

      {/* Visible text that will act as the legend of the fieldset. Hidden for screen readers. */}
      {legend && (
        <FormLabel className={classes.legend} component="p" aria-hidden="true" role="presentation">
          {legend}
        </FormLabel>
      )}

      <FormGroup>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              id={'phone-fieldset-' + id + '--alias'}
              label={intl.translate('account_phones_page__alias')}
              autoFocus={autoFocus}
              disabled={disabled}
              value={alias}
              onChange={(event) => onPhoneFieldChange('alias', event.target.value)}
              isValid={(value) => !!value}
              errorMessage={intl.translate('general__empty_field_error')}
            />
          </Grid>
          <Grid item xs={12}>
            <PhoneNumberInput
              id={'phone-fieldset-' + id + '--phone-number'}
              label={intl.translate('account_phones_page__phone_number')}
              disabled={disabled}
              confirmed={phoneNumberConfirmed}
              onConfirm={onConfirmPhoneNumber}
              value={phoneNumber}
              countryCodeValue={countryCode}
              onPhoneNumberChange={(event) => onPhoneFieldChange('phoneNumber', event.target.value)}
              onCountryCodeChange={(event) => onPhoneFieldChange('countryCode', event.target.value)}
              isValid={(value) => validate.isValidPhoneNumber(value, { countryCode })}
            />
            <CheckboxInput
              id={'phone-fieldset-' + id + '--phone-number-sms'}
              label={intl.translate('general__phone_number_sms')}
              disabled={disabled}
              checked={phoneNumberSms}
              onChange={(event) => onPhoneFieldChange('phoneNumberSms', event.target.checked)}
            />
          </Grid>
        </Grid>
      </FormGroup>

      {onDelete && (
        <Tooltip title={intl.translate('general__delete')}>
          {/* The <div> is necessary to avoid problems with the tooltip wrapping a disabled button */}
          <div className={classes['delete-btn']}>
            <IconButtonWithProgress
              showProgress={showDeleteProgress}
              aria-label={intl.translate('general__delete') + ' ' + legend}
              onClick={onDelete}
            >
              <DeleteIcon />
            </IconButtonWithProgress>
          </div>
        </Tooltip>
      )}
    </FormControl>
  )
}

export default PhoneFieldset
